import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import VisorCFDI from "./VisorCFDI";
import VisoresSatHubExportPanel from "@/components/VisoresSatHubExportPanel";
import { GitCompare, TrendingDown, UsersRound, Info } from "lucide-react";

/** Títulos del acordeón y del carril embebido solo en este hub (la ruta /visor no cambia). */
const SIMILAR_SAT_EMBED_LABELS = {
  emitido: "Visor SAT CFDI Emitidos",
  recibido: "Visor SAT CFDI Recibidos",
  nomina: "Visor SAT CFDI Nominas",
};

/**
 * Contenido del hub Similar SAT (aviso + acordeón con tres visores embebidos).
 * Se usa en la ruta `/visores-sat` y embebido en `/visor` (5ª sección).
 */
export default function VisoresSatHubBody({ wrapperClassName = "p-8 max-w-7xl mx-auto space-y-4" }) {
  const [openSections, setOpenSections] = useState([]);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const o = (searchParams.get("open") || "").toLowerCase();
    if (o === "ingresos" || o === "egresos" || o === "nominas") {
      setOpenSections((prev) => (prev.includes(o) ? prev : [...prev, o]));
    }
  }, [searchParams]);

  return (
    <div className={wrapperClassName}>
      <div
        className="rounded-sm border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 leading-relaxed flex gap-3"
        role="note"
        data-testid="visores-hub-carga-notice"
      >
        <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" strokeWidth={1.75} aria-hidden />
        <p>
          Revisa que se encuentren cargados <strong>todos los CFDI a la fecha</strong> en el sistema (de preferencia los{" "}
          <strong>últimos 2 años</strong>) para validar complementos de pago realizados con fechas anteriores, y así el
          sistema funcione correctamente. <span className="text-slate-600">(No sustituye a los visores del SAT.)</span>
        </p>
      </div>
      <VisoresSatHubExportPanel />
      <Accordion
        type="multiple"
        value={openSections}
        onValueChange={setOpenSections}
        className="rounded-sm border border-slate-200 bg-white shadow-sm"
      >
        <AccordionItem value="ingresos" className="border-slate-200 px-0">
          <AccordionTrigger>
            <span className="flex items-center gap-2">
              <GitCompare className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              {SIMILAR_SAT_EMBED_LABELS.emitido}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
            <div className="pt-4 overflow-x-auto">
              <VisorCFDI
                defaultView="emitido"
                forceSatPeriodLayout
                embedMode
                embedSectionLabels={SIMILAR_SAT_EMBED_LABELS}
                panelActive={openSections.includes("ingresos")}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="egresos" className="border-slate-200 px-0">
          <AccordionTrigger>
            <span className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              {SIMILAR_SAT_EMBED_LABELS.recibido}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
            <div className="pt-4 overflow-x-auto">
              <VisorCFDI
                defaultView="recibido"
                forceSatPeriodLayout
                embedMode
                embedSectionLabels={SIMILAR_SAT_EMBED_LABELS}
                panelActive={openSections.includes("egresos")}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="nominas" className="border-slate-200 px-0 border-b-0">
          <AccordionTrigger>
            <span className="flex items-center gap-2">
              <UsersRound className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              {SIMILAR_SAT_EMBED_LABELS.nomina}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
            <div className="pt-4 overflow-x-auto">
              <VisorCFDI
                defaultView="nomina"
                forceSatPeriodLayout
                embedMode
                embedSectionLabels={SIMILAR_SAT_EMBED_LABELS}
                panelActive={openSections.includes("nominas")}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
